import { addDaysToIsoDate } from '@/utils/date';

// Translates the user-facing "forecast window" (the target weeks they want to
// see) into the FCTs the charts actually query, and back. Users think in terms
// of "I need a forecast for next week through end of month" — i.e. target weeks
// — not forecast-creation times. A forecast point's target week is its FCT
// shifted forward by the lead time, and lead_time is expressed in DAYS (the raw
// API value — e.g. lead_time 7 = one week ahead), so:
//
//   targetWeek = FCT + leadTime days
//   FCT        = targetWeek − leadTime days
//
// Span doesn't move the target week (it's the forecast horizon length), so it
// plays no part in the FCT↔target-week mapping here.

function leadTimeDays(leadTime: string): number | null {
  // Empty string is "no lead time selected" — Number('') is 0, so guard it
  // explicitly before the numeric check.
  if (leadTime.trim() === '') return null;
  const lt = Number(leadTime);
  return Number.isFinite(lt) ? lt : null;
}

// Every target week reachable from the available FCTs at the given lead time.
// This is the set the date-range selector should offer — only weeks that map
// back to a real FCT exist. Returned sorted ISO (YYYY-MM-DD), deduped.
export function availableTargetWeeks(availableFcts: string[], leadTime: string): string[] {
  const lt = leadTimeDays(leadTime);
  if (lt == null) return [];
  const weeks = availableFcts.map((fct) => addDaysToIsoDate(fct, lt));
  return [...new Set(weeks)].sort();
}

// The FCTs that produce target weeks within [start, end] (inclusive) at the
// given lead time, restricted to FCTs that actually exist. Drives the charts.
export function fctsForWindow(
  availableFcts: string[],
  leadTime: string,
  start: string | undefined,
  end: string | undefined,
): string[] {
  const lt = leadTimeDays(leadTime);
  if (lt == null) return [];
  const availableSet = new Set(availableFcts);
  const fcts = availableFcts.filter((fct) => {
    const targetWeek = addDaysToIsoDate(fct, lt);
    if (start && targetWeek < start) return false;
    if (end && targetWeek > end) return false;
    return true;
  });
  // Already a subset of availableFcts, but keep the guard explicit + sorted.
  return [...new Set(fcts)].filter((fct) => availableSet.has(fct)).sort();
}
